import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { ExpandableContainer } from 'components/expandable-container';
import { TypeCurveGunBarrel } from './type-curve-gun-barrel';
import { TypeCurveHeader } from 'types/api-responses';
import { SlotTable } from './slot-table';
import style from './inventory-management-section.module.scss';

interface InventoryManagementSectionProps {
  data: TypeCurveHeader;
  refetchVerifications?: () => void;
  onInventoryVerdictChanged?: () => void;
  /** Inventory Workspace only: unpromoted, so unit verdicts can still change. */
  inventoryEditable?: boolean;
  /** Passed through to the gun barrel, which renders the Unit builder action.
   *  `null` opens a new empty draft (no unit selected). */
  onEditSlots?: (unit: { id: number; name: string } | null) => void;
}

export const InventoryManagementSection: React.FC<
  InventoryManagementSectionProps
> = ({
  data,
  refetchVerifications,
  onInventoryVerdictChanged,
  inventoryEditable,
  onEditSlots,
}) => {
  // The type curve's default Structure Grid Collection drives the Gun Barrel's
  // structure_grid_formations layer — taken straight from the header response.
  const structureGridCollectionId = data.structureGridCollectionId ?? null;

  const collectionIds = useMemo(() => {
    return structureGridCollectionId !== null
      ? [structureGridCollectionId]
      : undefined;
  }, [structureGridCollectionId]);

  return (
    <Accordion defaultExpanded className={style.accordionRoot}>
      <AccordionSummary>
        <h2 className={style.title}>Inventory Management</h2>
      </AccordionSummary>
      <AccordionDetails className={style.accordionDetails}>
        {/* The expand icon opens this same gun barrel + slot table pair in a
            modal (mock parity). `keepMounted` keeps the pair mounted across
            expand/collapse — it just restyles the wrapper into a modal, so the
            chart/table never remount and don't re-fetch. The tree below is
            deliberately STABLE (same element positions in both states — the header
            is a null slot when collapsed, the wrappers become `display: contents`)
            so React preserves the instances. The expanded view keeps the action
            bar but drops the Unit builder button (edits stay on the inline panel). */}
        <ExpandableContainer alignment="center" keepMounted>
          {({ isExpanded, expand }) => (
            <div
              className={isExpanded ? style.expandedScroll : style.keptContents}
            >
              <div
                className={
                  isExpanded ? style.expandedInner : style.keptContents
                }
              >
                {isExpanded && (
                  <div className={style.expandedHeader}>
                    <span className={style.expandedEyebrow}>Gun Barrel</span>
                    <h2 className={style.title}>Inventory Management</h2>
                  </div>
                )}
                {/* Always mounted (mock parity): the toolbar shows even with no
                    unit selected, and the chart renders empty with a "Select a
                    unit" prompt. The request is null without a unit (no fetch). */}
                <TypeCurveGunBarrel
                  structureGridCollectionId={structureGridCollectionId}
                  horizontalWellSpacingFt={data.horizontalWellSpacingFt ?? 200}
                  typeCurveId={data.id}
                  refetchVerifications={refetchVerifications}
                  onInventoryVerdictChanged={onInventoryVerdictChanged}
                  inventoryEditable={inventoryEditable}
                  onEditSlots={isExpanded ? undefined : onEditSlots}
                  onExpand={isExpanded ? undefined : expand}
                  hideUnitBuilderButton={isExpanded}
                />
                <SlotTable
                  typeCurveHeaderDetailsId={data.id}
                  collectionIds={collectionIds}
                />
              </div>
            </div>
          )}
        </ExpandableContainer>
      </AccordionDetails>
    </Accordion>
  );
};
